import { City } from '../../types/City';
import { ExpenseCategory, SelectedExpense } from '../../types/ExpenseCategory';
import { formatCurrency } from '../formatters';

/**
 * Calcula el costo ajustado basado en el índice de costo de vida de la ciudad
 * @param baseCost Costo base en MXN para Ciudad de México (índice 45)
 * @param cityIndex Índice de costo de vida de la ciudad
 * @returns Costo ajustado para la ciudad específica
 */
export const calculateAdjustedCost = (baseCost: number, cityIndex: number): number => {
  // Ciudad de México tiene índice 45, calculamos el factor relativo
  const relativeFactor = cityIndex / 45;
  return Math.round(baseCost * relativeFactor);
};

/**
 * Calcula el total de gastos seleccionados
 * @param selectedExpenses Gastos seleccionados por el usuario
 * @param expenseCategories Categorías de gastos
 * @param selectedCity Ciudad seleccionada
 * @returns Total de gastos mensuales
 */
export const calculateTotalExpenses = (
  selectedExpenses: Record<string, SelectedExpense>,
  expenseCategories: ExpenseCategory[],
  selectedCity: City
): number => {
  let total = 0;
  
  Object.entries(selectedExpenses).forEach(([expenseId, config]) => {
    const expense = expenseCategories
      .flatMap(cat => cat.items)
      .find(item => item.id === expenseId);
    
    if (expense) {
      const adjustedCost = calculateAdjustedCost(expense.baseCost, selectedCity.costOfLiving);
      if (config.customAmount !== undefined) {
        total += config.customAmount;
      } else {
        total += adjustedCost * config.quantity;
      }
    }
  });
  
  return total;
};

/**
 * Calcula métricas de asequibilidad
 * @param monthlyIncome Ingreso mensual disponible
 * @param totalExpenses Total de gastos mensuales
 * @returns Métricas de asequibilidad
 */
export const calculateAffordabilityMetrics = (
  monthlyIncome: number, 
  totalExpenses: number
) => {
  const remainingIncome = monthlyIncome - totalExpenses;
  const affordabilityPercentage = monthlyIncome > 0 ? (totalExpenses / monthlyIncome) * 100 : 0;
  
  let affordabilityLevel: 'excellent' | 'good' | 'tight' | 'insufficient';
  let affordabilityMessage: string;
  
  if (affordabilityPercentage <= 70) {
    affordabilityLevel = 'excellent';
    affordabilityMessage = 'Estilo de vida muy cómodo';
  } else if (affordabilityPercentage <= 90) {
    affordabilityLevel = 'good';
    affordabilityMessage = 'Estilo de vida ajustado';
  } else if (affordabilityPercentage <= 100) {
    affordabilityLevel = 'tight';
    affordabilityMessage = 'Presupuesto muy ajustado';
  } else {
    affordabilityLevel = 'insufficient';
    affordabilityMessage = 'Presupuesto insuficiente';
  }
  
  return {
    remainingIncome,
    affordabilityPercentage,
    affordabilityLevel,
    affordabilityMessage
  };
};

/**
 * Genera recomendaciones basadas en el análisis de asequibilidad
 */
export const generateRecommendations = (
  affordabilityMetrics: ReturnType<typeof calculateAffordabilityMetrics>,
  selectedCity: City,
  monthlyIncome: number
): string[] => {
  const recommendations: string[] = [];
  
  if (affordabilityMetrics.affordabilityLevel === 'excellent') {
    recommendations.push(
      `¡Excelente! Puedes mantener este estilo de vida en ${selectedCity.name} con un excedente de ${formatCurrency(affordabilityMetrics.remainingIncome, 'MXN')} mensuales.`
    );
    recommendations.push(
      'Considera invertir el excedente en más certificados para acelerar tu crecimiento patrimonial.'
    );
  } else if (affordabilityMetrics.affordabilityLevel === 'good') {
    recommendations.push(
      `Puedes mantener este estilo de vida en ${selectedCity.name} de manera cómoda.`
    );
    recommendations.push(
      'Tu presupuesto está bien balanceado con un pequeño margen de seguridad.'
    );
  } else if (affordabilityMetrics.affordabilityLevel === 'tight') {
    recommendations.push(
      `El presupuesto está muy ajustado en ${selectedCity.name}. Considera reducir algunos gastos opcionales.`
    );
    recommendations.push(
      'Evalúa aumentar tu inversión en certificados para incrementar tus ingresos futuros.'
    );
  } else {
    recommendations.push(
      `Necesitarías ${formatCurrency(Math.abs(affordabilityMetrics.remainingIncome), 'MXN')} adicionales para mantener este estilo de vida en ${selectedCity.name}.`
    );
    recommendations.push(
      'Considera ajustar tus gastos o aumentar tu inversión en Cosecha Capital.'
    );
    recommendations.push(
      'Alternativamente, podrías explorar ciudades con menor costo de vida.'
    );
  }
  
  return recommendations;
};

/**
 * Función para calcular certificados necesarios para un estilo de vida
 */
export const calculateCertificatesForLifestyle = (
  targetExpenses: number,
  certificateMonthlyIncome: number
): number => {
  return Math.ceil(targetExpenses / certificateMonthlyIncome);
};